import { z } from 'zod';

export const exerciseStatusEnum = z.enum(['active', 'archived']);
export type ExerciseStatus = z.infer<typeof exerciseStatusEnum>;

export const exerciseTypeEnum = z.enum(['worksheet', 'activity', 'journal']);
export type ExerciseType = z.infer<typeof exerciseTypeEnum>;

export const exerciseDifficultyEnum = z.enum(['beginner', 'intermediate', 'advanced']);
export type ExerciseDifficulty = z.infer<typeof exerciseDifficultyEnum>;

export const clientExerciseStatusEnum = z.enum(['pending', 'in_progress', 'completed', 'reviewed']);
export type ClientExerciseStatus = z.infer<typeof clientExerciseStatusEnum>;

// ─── Template Fields (shared by worksheets, activities, journals) ────

export const FIELD_TYPES = [
  'text',
  'scale',
  'percentage',
  'dropdown',
  'radio',
  'multi_select',
  'yes_no',
  'date',
  'multi_entry',
] as const;
export const fieldTypeEnum = z.enum(FIELD_TYPES);
export type FieldType = z.infer<typeof fieldTypeEnum>;

/**
 * Template field. Mirrors `templateFieldSchema` in
 * `elixaa-core/elixaa-api/src/schemas/exerciseSchema.ts`.
 *
 * Type-specific extras live on the same flat object — the BE schema is
 * intentionally tolerant about unknown extras on irrelevant types.
 */
export const templateFieldSchema = z.object({
  id: z.string().min(1).max(50),
  label: z.string().min(1).max(500),
  type: fieldTypeEnum,
  required: z.boolean().optional(),
  // scale / percentage
  min: z.number().optional(),
  max: z.number().optional(),
  // dropdown / radio / multi_select
  options: z.array(z.string().max(200)).max(50).optional(),
  allowOther: z.boolean().optional(),
  // multi_entry
  entryCount: z.number().int().min(1).max(20).optional(),
  entryLabel: z.string().max(200).optional(),
});
export type TemplateField = z.infer<typeof templateFieldSchema>;

export const templateStructureSchema = z
  .object({
    fields: z.array(templateFieldSchema).min(1).max(100),
  })
  .refine(
    (data) => {
      const ids = data.fields.map((f) => f.id);
      return new Set(ids).size === ids.length;
    },
    { message: 'Field IDs must be unique within a template' },
  );
export type TemplateStructure = z.infer<typeof templateStructureSchema>;

// ─── Exercise Template ────────────────────────────────────────────────

export const exerciseSchema = z.object({
  id: z.string(),
  businessAssociateId: z.string().nullish(),
  createdById: z.string().nullish(),
  title: z.string(),
  type: exerciseTypeEnum,
  description: z.string().nullish(),
  // Free-text instructions appear on detail responses; not strictly part
  // of the canonical Exercise row but the FE has surfaced this slot since
  // the original assign UI shipped, so we keep it tolerant here.
  instructions: z.string().nullish(),
  category: z.string().nullish(),
  difficulty: exerciseDifficultyEnum.nullish(),
  estimatedTime: z.string().nullish(),
  tags: z.array(z.string()).nullish(),
  isStandard: z.boolean(),
  structure: templateStructureSchema,
  status: exerciseStatusEnum,
  // Admin-only pair. `s3ThumbnailKey` tells the upload field whether artwork is
  // already attached; `thumbnailDownloadUrl` is the presigned preview. The BE
  // redacts `s3ThumbnailKey` entirely (absent, not null) for non-admin roles,
  // hence `nullish()` rather than `nullable()`. Client portal schemas
  // deliberately carry only `thumbnailDownloadUrl` — see
  // features/engagement/types/exercise.ts.
  s3ThumbnailKey: z.string().nullish(),
  thumbnailDownloadUrl: z.string().nullish(),
  // How the artwork should be COMPOSED on a card — contained on the card's own
  // surface (an illustration) vs full-bleed (a photograph or video still).
  // Set at upload time by decoding the file; see `detectTransparentArtwork`.
  // `nullish()` because it is genuinely three-valued: rows uploaded before this
  // existed carry null, and null must read as "not an illustration" so nothing
  // re-renders. NOT admin-only — the client portal needs it to draw the card.
  thumbnailIsIllustration: z.boolean().nullish(),
  createdAt: z.string(),
  updatedAt: z.string().nullish(),
  deletedAt: z.string().nullish(),
});
export type Exercise = z.infer<typeof exerciseSchema>;

// ─── Template Thumbnail Upload ────────────────────────────────────────

/**
 * Content types accepted by the template thumbnail presign endpoint.
 * Anything else (including other image types the underlying S3 allowlist
 * would otherwise permit) is rejected server-side with a 400 before
 * reaching S3 — mirror the enum here so the failure happens client-side
 * with a clear message instead of a round trip.
 */
export const templateThumbnailContentTypeEnum = z.enum([
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
]);
export type TemplateThumbnailContentType = z.infer<typeof templateThumbnailContentTypeEnum>;

/**
 * Presign response for a template thumbnail upload.
 *
 * The BE persists the derived S3 key onto the row inside the same transaction
 * before returning, so the caller does NOT patch the key afterwards — it only
 * PUTs the bytes and invalidates the detail query.
 *
 * Declared here and re-exported from `features/assessments/types/schemas.ts`
 * rather than duplicated — both presign endpoints return the identical shape.
 */
export const templateUploadUrlResponseSchema = z.object({
  uploadUrl: z.string().min(1),
  key: z.string().min(1),
});
export type TemplateUploadUrlResponse = z.infer<typeof templateUploadUrlResponseSchema>;

export const requestExerciseThumbnailUploadUrlSchema = z.object({
  exerciseId: z.string().min(1),
  contentType: templateThumbnailContentTypeEnum,
  filename: z.string().min(1),
  // Rides the presign request rather than a follow-up PATCH so the BE writes it
  // in the same transaction as `s3ThumbnailKey` — otherwise there is a window
  // where the row points at new artwork carrying the PREVIOUS image's
  // composition. Optional: omitted when detection could not decide, and the BE
  // then clears the column rather than leaving a stale value.
  isIllustration: z.boolean().optional(),
});
export type RequestExerciseThumbnailUploadUrlInput = z.infer<
  typeof requestExerciseThumbnailUploadUrlSchema
>;

// Shape returned by LIST endpoint (joined, flat fields).
export const clientExerciseSchema = z.object({
  id: z.string(),
  clientId: z.string(),
  exerciseId: z.string(),
  assignedById: z.string().nullish(),
  sessionId: z.string().nullish(),
  status: clientExerciseStatusEnum,
  dueDate: z.string().nullish(),
  completedAt: z.string().nullish(),
  reviewedAt: z.string().nullish(),
  createdAt: z.string(),
  exerciseTitle: z.string(),
  exerciseType: z.string().nullish(),
  exerciseCategory: z.string().nullish(),
  exerciseDifficulty: z.string().nullish(),
  exerciseEstimatedTime: z.string().nullish(),
});
export type ClientExercise = z.infer<typeof clientExerciseSchema>;

// BE nested exercise block — returned by assign POST and detail GET.
const nestedExerciseSchema = z
  .object({
    id: z.string().optional(),
    title: z.string().optional(),
    type: z.string().nullish(),
    category: z.string().nullish(),
    difficulty: z.string().nullish(),
    estimatedTime: z.string().nullish(),
  })
  .nullish();

// Shape returned by POST /clients/:id/exercises. Row columns are flat
// and the exercise catalog entry is nested under `exercise`.
export const clientExerciseAssignResponseSchema = z
  .object({
    id: z.string(),
    clientId: z.string(),
    exerciseId: z.string(),
    assignedById: z.string().nullish(),
    sessionId: z.string().nullish(),
    status: clientExerciseStatusEnum,
    dueDate: z.string().nullish(),
    completedAt: z.string().nullish(),
    reviewedAt: z.string().nullish(),
    createdAt: z.string(),
    exercise: nestedExerciseSchema,
  })
  .transform<ClientExercise>((r) => ({
    id: r.id,
    clientId: r.clientId,
    exerciseId: r.exerciseId,
    assignedById: r.assignedById,
    sessionId: r.sessionId,
    status: r.status,
    dueDate: r.dueDate,
    completedAt: r.completedAt,
    reviewedAt: r.reviewedAt,
    createdAt: r.createdAt,
    exerciseTitle: r.exercise?.title ?? '',
    exerciseType: r.exercise?.type ?? null,
    exerciseCategory: r.exercise?.category ?? null,
    exerciseDifficulty: r.exercise?.difficulty ?? null,
    exerciseEstimatedTime: r.exercise?.estimatedTime ?? null,
  }));

// Consumer-facing detail type — flat.
export interface ClientExerciseDetail extends ClientExercise {
  response?: Record<string, unknown> | null;
  proofUrl?: string | null;
  proofContentType?: string | null;
  reviewNotes?: string | null;
  instructions?: string | null;
}

// Shape returned by GET /clients/:id/exercises/:cxId. Safe row fields +
// decrypted PHI + nested `exercise`. Flatten to ClientExerciseDetail.
export const clientExerciseDetailResponseSchema = z
  .object({
    id: z.string(),
    clientId: z.string(),
    exerciseId: z.string(),
    assignedById: z.string().nullish(),
    sessionId: z.string().nullish(),
    status: clientExerciseStatusEnum,
    dueDate: z.string().nullish(),
    completedAt: z.string().nullish(),
    reviewedAt: z.string().nullish(),
    createdAt: z.string(),
    exercise: nestedExerciseSchema,
    instructions: z.string().nullish(),
    response: z.record(z.string(), z.unknown()).nullish(),
    proofUrl: z.string().nullish(),
    proofContentType: z.string().nullish(),
    reviewNotes: z.string().nullish(),
  })
  .transform<ClientExerciseDetail>((r) => ({
    id: r.id,
    clientId: r.clientId,
    exerciseId: r.exerciseId,
    assignedById: r.assignedById,
    sessionId: r.sessionId,
    status: r.status,
    dueDate: r.dueDate,
    completedAt: r.completedAt,
    reviewedAt: r.reviewedAt,
    createdAt: r.createdAt,
    exerciseTitle: r.exercise?.title ?? '',
    exerciseType: r.exercise?.type ?? null,
    exerciseCategory: r.exercise?.category ?? null,
    exerciseDifficulty: r.exercise?.difficulty ?? null,
    exerciseEstimatedTime: r.exercise?.estimatedTime ?? null,
    instructions: r.instructions ?? null,
    response: r.response ?? null,
    proofUrl: r.proofUrl ?? null,
    proofContentType: r.proofContentType ?? null,
    reviewNotes: r.reviewNotes ?? null,
  }));

// Shape returned by POST /clients/:id/exercises/:cxId/review.
export const reviewExerciseResponseSchema = z.object({
  clientExerciseId: z.string(),
  status: clientExerciseStatusEnum,
});
export type ReviewExerciseResponse = z.infer<typeof reviewExerciseResponseSchema>;

export const assignExerciseSchema = z.object({
  exerciseId: z.string(),
  sessionId: z.string().optional(),
  dueDate: z.string().optional(),
  instructions: z.string().max(5000).optional(),
});
export type AssignExerciseInput = z.infer<typeof assignExerciseSchema>;

export const reviewExerciseSchema = z.object({
  reviewNotes: z.string().max(5000).optional(),
});
export type ReviewExerciseInput = z.infer<typeof reviewExerciseSchema>;

// ─── Template Create / Update Inputs ──────────────────────────────────

/**
 * Mirrors `createExerciseSchema` in
 * `elixaa-core/elixaa-api/src/schemas/exerciseSchema.ts`.
 */
export const createExerciseInputSchema = z.object({
  title: z.string().min(1).max(255),
  type: exerciseTypeEnum,
  description: z.string().max(2000).optional(),
  category: z.string().max(100).optional(),
  difficulty: exerciseDifficultyEnum.optional(),
  estimatedTime: z.string().max(50).optional(),
  tags: z.array(z.string().max(50)).max(20).optional(),
  structure: templateStructureSchema,
});
export type CreateExerciseInput = z.infer<typeof createExerciseInputSchema>;

export const updateExerciseInputSchema = createExerciseInputSchema.partial().extend({
  // Clear-only. The presign endpoint owns setting this; `null` removes artwork.
  s3ThumbnailKey: z.null().optional(),
});
export type UpdateExerciseInput = z.infer<typeof updateExerciseInputSchema>;

export interface ExerciseListParams {
  page?: number;
  limit?: number;
  sortBy?: 'createdAt' | 'updatedAt' | 'title';
  sortOrder?: 'asc' | 'desc';
  category?: string;
  search?: string;
  type?: ExerciseType[];
  status?: ExerciseStatus[];
  difficulty?: ExerciseDifficulty[];
}

export interface ClientExerciseListParams {
  page?: number;
  limit?: number;
  status?: ClientExerciseStatus;
}

export interface PaginatedExercises {
  data: Exercise[];
  pagination: { page: number; limit: number; total: number };
}

export interface PaginatedClientExercises {
  data: ClientExercise[];
  pagination: { page: number; limit: number; total: number };
}

/** Row returned by `GET /exercises/review-queue`. */
export const exerciseReviewQueueRowSchema = z.object({
  id: z.string(),
  clientId: z.string(),
  exerciseId: z.string(),
  assignedById: z.string(),
  status: clientExerciseStatusEnum,
  completedAt: z.string().nullable(),
  reviewedAt: z.string().nullable(),
  createdAt: z.string(),
  dueDate: z.string().nullable(),
  exerciseTitle: z.string(),
  exerciseType: exerciseTypeEnum,
  exerciseCategory: z.string().nullable(),
  clientFirstName: z.string().nullable(),
  clientLastName: z.string().nullable(),
  clientEmail: z.string().nullable(),
});
export type ExerciseReviewQueueRow = z.infer<typeof exerciseReviewQueueRowSchema>;

/** Status filter accepted by `GET /exercises/review-queue`. `assigned`
 * collapses the BE-level `pending` and `in_progress` rows (the exercise
 * is out with the client and not yet submitted). */
export const reviewQueueStatusEnum = z.enum(['assigned', 'completed', 'reviewed']);
export type ReviewQueueStatus = z.infer<typeof reviewQueueStatusEnum>;

export const exerciseReviewQueueResponseSchema = z.array(exerciseReviewQueueRowSchema);
